/**
 * JobCosts — labor spent vs estimate per job, ranked worst overrun first.
 * Data: GET /api/timeclock/job-costs (approved punches × burdened rate).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { DollarSign, Download } from "lucide-react";

export default function JobCosts() {
  const [rows, setRows] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    api.get("/timeclock/job-costs").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  const visible = useMemo(
    () => (rows || []).filter((r) => showClosed || !r.closed),
    [rows, showClosed],
  );
  const closedCount = (rows || []).filter((r) => r.closed).length;

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Job", "Status", "Approved Hours", "Actual Labor (burdened)", "Estimated Labor", "Variance", "Budget Used %", "Field Expenses"];
    const lines = visible.map((r) => [
      esc(r.title), esc(r.closed ? "Closed" : r.status),
      r.hours, r.labor_actual.toFixed(2),
      r.labor_estimated > 0 ? r.labor_estimated.toFixed(2) : "",
      r.labor_estimated > 0 ? r.variance.toFixed(2) : "",
      r.labor_estimated > 0 ? ((r.labor_actual / r.labor_estimated) * 100).toFixed(0) : "",
      r.field_expenses.toFixed(2),
    ].join(","));
    const blob = new Blob([header.join(",") + "\n" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `job_costs_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-6 lg:p-8" data-testid="job-costs-page">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <DollarSign className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Job Labor Costs</h1>
        {closedCount > 0 && (
          <label className="ml-auto inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="job-costs-show-closed" />
            Include closed-out jobs ({closedCount})
          </label>
        )}
        <button
          onClick={exportCsv}
          disabled={visible.length === 0}
          data-testid="job-costs-export"
          className={`${closedCount > 0 ? "" : "ml-auto "}h-9 px-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-6">Approved Time Clock hours × burdened rate vs the labor estimate on each deal. Worst overruns first.</p>

      {rows === null && <div className="text-sm text-zinc-400">Loading…</div>}
      {rows !== null && visible.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="job-costs-empty">
          No jobs with approved time yet. Hours appear here once crew punches are approved on Time &amp; Expenses.
        </div>
      )}

      {visible.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="job-costs-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                <th className="px-4 py-2.5">Job</th>
                <th className="px-3 py-2.5 text-right">Hours</th>
                <th className="px-3 py-2.5 text-right">Actual Labor</th>
                <th className="px-3 py-2.5 text-right">Est. Labor</th>
                <th className="px-3 py-2.5 text-right">Variance</th>
                <th className="px-3 py-2.5 text-right">Field Exp.</th>
                <th className="px-4 py-2.5 text-right">Budget Used</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const over = r.variance > 0 && r.labor_estimated > 0;
                const pctUsed = r.labor_estimated > 0 ? (r.labor_actual / r.labor_estimated) * 100 : null;
                return (
                  <tr key={r.deal_id} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`job-cost-row-${r.deal_id}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/projects/${r.deal_id}`} className="font-bold hover:text-blue-700">{r.title}</Link>
                      {r.closed && <span className="ml-2 text-[9px] font-bold uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded-sm px-1.5 py-0.5">Closed</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.hours}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{formatCurrency(r.labor_actual)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-500">{r.labor_estimated > 0 ? formatCurrency(r.labor_estimated) : "—"}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold ${r.labor_estimated <= 0 ? "text-zinc-400" : over ? "text-red-700" : "text-emerald-700"}`}>
                      {r.labor_estimated > 0 ? `${r.variance > 0 ? "+" : ""}${formatCurrency(r.variance)}` : "no estimate"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-500">{r.field_expenses > 0 ? formatCurrency(r.field_expenses) : "—"}</td>
                    <td className="px-4 py-2.5">
                      {pctUsed === null ? (
                        <span className="text-[10px] text-zinc-400 block text-right">—</span>
                      ) : (
                        <div className="flex items-center gap-2 justify-end">
                          <div className="w-24 h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                            <div className={`h-full ${pctUsed > 100 ? "bg-red-600" : pctUsed > 80 ? "bg-amber-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, pctUsed)}%` }} />
                          </div>
                          <span className={`font-mono text-xs font-bold ${pctUsed > 100 ? "text-red-700" : "text-zinc-600"}`}>{pctUsed.toFixed(0)}%</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] text-zinc-400 mt-3">Set the labor estimate on each deal (Financials → Labor, or the Live P&L Labor row). Burden multiplier and pay rates live under Time &amp; Expenses / the Employee record.</div>
    </div>
  );
}
